// Contenu du centre d'aide — bilingue FR/EN, filtré par rôle dans HelpCenterPage.
// minRole: "all" (tout le monde) | "manage" (admin + trésorier + responsable musical)
export const HELP_SECTIONS = [
  {
    id: "start", icon: "Rocket", minRole: "all",
    fr: {
      title: "Prise en main",
      points: [
        "Le tableau de bord résume l'essentiel : prochain événement, répétitions à venir, activité récente du groupe.",
        "Répondez à vos disponibilités dès la fenêtre « Prochain événement » ou depuis la fiche de l'événement.",
        "Installez l'application sur votre téléphone (menu ⋮ ou bouton de partage → « Ajouter à l'écran d'accueil ») pour l'ouvrir comme une vraie app et recevoir les notifications.",
        "Votre espace personnel (menu « Mes groupes ») contient vos partitions et favoris privés, invisibles par le groupe.",
      ],
    },
    en: {
      title: "Getting started",
      points: [
        "The dashboard sums up what matters: next event, upcoming rehearsals, recent group activity.",
        "Answer your availability from the “Next event” popup or from the event page.",
        "Install the app on your phone (⋮ menu or share button → “Add to Home Screen”) to open it like a native app and receive notifications.",
        "Your personal space (“My groups” menu) holds your private scores and favorites, invisible to the group.",
      ],
    },
  },
  {
    id: "agenda", icon: "CalendarDays", minRole: "all",
    fr: {
      title: "Agenda et présences",
      points: [
        "Chaque événement (répétition, concert, prestation) a sa fiche : date, lieu avec itinéraire, tenue, programme musical, commentaires.",
        "Indiquez votre présence en un geste : « Je viens / Je ne viens pas » directement sur la fiche ou la vignette. Vous pouvez changer d'avis à tout moment.",
        "Le bouton cloche permet à l'administration de relancer uniquement les membres qui n'ont pas répondu.",
        "Exportez un événement vers votre calendrier personnel (fichier .ics) ou partagez-le.",
        "Les événements récurrents (ex. répétition hebdomadaire) se créent une seule fois avec la fréquence choisie.",
      ],
    },
    en: {
      title: "Agenda & attendance",
      points: [
        "Each event (rehearsal, concert, gig) has its own page: date, venue with directions, dress code, musical program, comments.",
        "Answer your attendance in one gesture: “I'm coming / I can't make it” on the page or the card. You can change your mind anytime.",
        "The bell button lets admins remind only the members who have not answered yet.",
        "Export an event to your personal calendar (.ics file) or share it.",
        "Recurring events (e.g. weekly rehearsal) are created once with the chosen frequency.",
      ],
    },
  },
  {
    id: "library", icon: "LibraryBig", minRole: "all",
    fr: {
      title: "Bibliothèque et partitions",
      points: [
        "Les morceaux sont organisés par catégories (marches, jigs…), puis par versions et pupitres (cornemuse, caisse claire…).",
        "Ouvrez une partition PDF en plein écran ; fermez avec la croix en haut à droite (ou un glissement vers la droite sur mobile).",
        "L'étoile ajoute un morceau ou un fichier à vos Favoris pour le retrouver instantanément.",
        "La recherche en haut de la bibliothèque filtre par nom, compositeur ou étiquette.",
        "Les partitions marquées « hors-ligne » restent consultables sans connexion (idéal en déplacement).",
      ],
    },
    en: {
      title: "Library & scores",
      points: [
        "Tunes are organized by categories (marches, jigs…), then by versions and sections (pipes, snare…).",
        "Open a PDF score fullscreen; close it with the cross at the top right (or a right swipe on mobile).",
        "The star adds a tune or file to your Favorites to find it instantly.",
        "The search bar at the top of the library filters by name, composer or tag.",
        "Scores marked “offline” remain available without a connection (great on the road).",
      ],
    },
  },
  {
    id: "player", icon: "Music2", minRole: "all",
    fr: {
      title: "Lecteur audio et vidéo",
      points: [
        "Le lecteur audio permet de ralentir un morceau (vitesse 0,5× à 1,2×) sans changer la tonalité — parfait pour déchiffrer.",
        "Posez des repères (parties A, B…) pour sauter directement au passage à travailler ; la boucle A-B répète un passage en continu.",
        "Sur mobile, l'audio continue en arrière-plan : la mini-barre en bas garde la lecture sous contrôle pendant que vous naviguez.",
        "Les vidéos exotiques (MOV d'iPhone…) sont automatiquement converties pour être lisibles partout — une barre de progression discrète s'affiche pendant la conversion.",
        "Glissez vers la droite pour fermer un média ou minimiser le lecteur audio.",
      ],
    },
    en: {
      title: "Audio & video player",
      points: [
        "The audio player slows a tune down (0.5× to 1.2×) without changing pitch — perfect for learning by ear.",
        "Drop markers (part A, B…) to jump straight to the tricky passage; the A-B loop repeats it continuously.",
        "On mobile, audio keeps playing in the background: the mini-bar at the bottom keeps playback under control while you browse.",
        "Exotic videos (iPhone MOV…) are automatically converted to play everywhere — a discreet progress bar shows during conversion.",
        "Swipe right to close a media or minimize the audio player.",
      ],
    },
  },
  {
    id: "program", icon: "ListMusic", minRole: "all",
    fr: {
      title: "Programmes et setlists",
      points: [
        "Le programme d'une répétition se construit en piochant les morceaux de la bibliothèque, rangés par blocs de travail (ensemble, pupitres…).",
        "Chaque morceau du programme accepte une note (ex. « retravailler l'intro »).",
        "Les setlists ordonnent les morceaux pour les concerts ; elles sont réutilisables d'un événement à l'autre.",
        "Les programmes et setlists sont visibles par tout le groupe : chacun sait quoi préparer.",
      ],
    },
    en: {
      title: "Programs & setlists",
      points: [
        "A rehearsal program is built by picking tunes from the library, arranged into work blocks (full band, sections…).",
        "Each program tune accepts a note (e.g. “rework the intro”).",
        "Setlists order tunes for concerts; they can be reused from one event to another.",
        "Programs and setlists are visible to the whole group: everyone knows what to prepare.",
      ],
    },
  },
  {
    id: "fees", icon: "HandCoins", minRole: "all",
    fr: {
      title: "Ma cotisation",
      points: [
        "La carte « Ma cotisation » (tableau de bord ou menu) indique le montant, l'échéance et le RIB du groupe avec boutons « Copier ».",
        "Après votre virement, appuyez sur « J'ai payé » : votre cotisation passe « en attente » jusqu'à validation par l'administration.",
        "Une bannière vous rappelle une cotisation en retard ; une fois validée, tout disparaît.",
      ],
    },
    en: {
      title: "My membership fee",
      points: [
        "The “My fee” card (dashboard or menu) shows the amount, due date and the group's bank details with “Copy” buttons.",
        "After your bank transfer, tap “I paid”: your fee turns “pending” until the administration validates it.",
        "A banner reminds you of an overdue fee; once validated, everything disappears.",
      ],
    },
  },
  {
    id: "docs", icon: "FolderOpen", minRole: "all",
    fr: {
      title: "Documents",
      points: [
        "L'espace Documents fonctionne comme un explorateur de fichiers : dossiers, sous-dossiers, grille ou liste.",
        "PV d'assemblée, règlement, photos… tout est partagé ici, avec recherche et aperçu.",
        "Comme la bibliothèque, les documents peuvent être gardés hors-ligne.",
      ],
    },
    en: {
      title: "Documents",
      points: [
        "The Documents area works like a file explorer: folders, sub-folders, grid or list view.",
        "Meeting minutes, rules, photos… everything is shared here, with search and preview.",
        "Like the library, documents can be kept offline.",
      ],
    },
  },
  {
    id: "inventory", icon: "Package", minRole: "all",
    fr: {
      title: "Inventaire et prêts",
      points: [
        "L'inventaire liste le matériel du groupe (uniformes, instruments, accessoires) et qui détient quoi.",
        "Si on vous prête du matériel, vous le voyez dans votre espace ; signalez tout problème à l'administration.",
      ],
    },
    en: {
      title: "Inventory & loans",
      points: [
        "The inventory lists the group's gear (uniforms, instruments, accessories) and who holds what.",
        "If gear is lent to you, it appears in your area; report any issue to the administration.",
      ],
    },
  },
  {
    id: "pwa", icon: "Bell", minRole: "all",
    fr: {
      title: "Notifications et installation",
      points: [
        "Activez les notifications (cloche en haut) pour être prévenu des nouveaux événements, partitions et messages.",
        "Cliquer sur une notification ouvre directement la bonne page dans l'app.",
        "Sans connexion, l'app conserve vos partitions et pages déjà vues ; tout se resynchronise au retour du réseau.",
      ],
    },
    en: {
      title: "Notifications & install",
      points: [
        "Enable notifications (bell at the top) to be told about new events, scores and messages.",
        "Tapping a notification opens the right page directly in the app.",
        "Offline, the app keeps your previously viewed scores and pages; everything re-syncs when the network returns.",
      ],
    },
  },
  {
    id: "admin", icon: "ShieldCheck", minRole: "manage",
    fr: {
      title: "Administration du groupe",
      points: [
        "Membres : invitez par e-mail (invitation à durée limitée) ou créez un accès direct ; attribuez les rôles (administrateur, responsable musical, trésorier, musicien, membre non-musicien).",
        "Cotisations : créez les périodes (saison, montant, échéance), suivez qui a payé, validez les « J'ai payé », relancez les retardataires en un clic.",
        "Présences : la matrice par événement/répétition donne la disponibilité de tous ; exportez ou pointez à l'entrée.",
        "Personnalisation : nom, logo, couleurs et instruments du groupe se règlent dans Paramètres → Groupe et Thème.",
        "Le trésorier voit et gère uniquement les cotisations ; le responsable musical gère la bibliothèque et les événements.",
      ],
    },
    en: {
      title: "Group administration",
      points: [
        "Members: invite by e-mail (time-limited invitation) or create direct access; assign roles (admin, music leader, treasurer, musician, non-musician member).",
        "Fees: create periods (season, amount, due date), track who paid, validate “I paid” claims, remind latecomers in one click.",
        "Attendance: the per-event/rehearsal matrix shows everyone's availability; export or check people in.",
        "Customization: name, logo, colors and instruments are set in Settings → Group and Theme.",
        "The treasurer only sees and manages fees; the music leader manages the library and events.",
      ],
    },
  },
];
